/**
 * questionari/registro — il registro delle azioni di un'azienda nel CRM di piattaforma (console).
 * ============================================================================
 *
 * Simone, 20/10/2026: tracciare nella console, sezione Questionari, «come una sorta di CRM che ci dica
 * sempre in modo programmatico cosa abbiamo fatto con quell'utente e a che punto siamo arrivati», senza
 * niente di fatto a mano. Ogni cosa successa con un'azienda iscritta lascia UNA voce in
 * `platform_leads/{id}/registro/{voceId}`; lo STATO della scheda e il PROSSIMO PASSO li ricava il server
 * dalle voci (`aggiornaLeadDalRegistro`), sempre con le regole di questo file: chi scrive una voce (una
 * funzione del server, la skill /probusiness) non tocca lo stato.
 *
 * Identificatori ≠ etichette (regola cardine #1): `tipo`, `da` e i codici del prossimo passo sono id ASCII
 * fissi; le parole che si leggono stanno nei dizionari della console.
 *
 * L'id di una voce è l'impronta della sua CHIAVE (`chiaveVoce`): la stessa cosa registrata due volte (un
 * trigger che riprova, la skill rilanciata) resta una voce sola. La skill (Python) calcola l'id allo
 * stesso modo: sha256 della chiave, primi 24 caratteri esadecimali.
 *
 * File puro: nessun import. Il server lo compila col target ES2017.
 *
 * Progetto: docs/01-ARCHITECTURE/ACCESSO-ANTICIPATO-MODEL.md § 3.
 */

/** La sottoraccolta delle voci, sotto `platform_leads/{id}`. La scrive solo il server (e la skill con le credenziali di piattaforma). */
export const SOTTORACCOLTA_REGISTRO_LEAD = 'registro';

/** Gli stati del percorso di un'azienda (ordine del percorso, non alfabetico). */
export const STATI_LEAD_PIATTAFORMA = [
  'nuovo',
  'contattato',
  'questionario_inviato',
  'questionario_completato',
  'demo',
  'pilota',
  'convertito',
  'perso',
] as const;
export type StatoLeadPiattaforma = (typeof STATI_LEAD_PIATTAFORMA)[number];

/** I tipi di voce. Si aggiungono in fondo; un id non si rinomina mai (è nelle voci già scritte). */
export const TIPI_VOCE_REGISTRO = [
  'iscrizione',
  'questionario_compilato',
  'email_conferma',
  'messaggio',
  'risposta_ricevuta',
  'questionario_inviato',
  'accesso_attivato',
  'tetto_raggiunto',
  'proposta_pagamento',
  'passato_a_pagamento',
  'pausa_inattivita',
  'uscito',
  'nota',
] as const;
export type TipoVoceRegistro = (typeof TIPI_VOCE_REGISTRO)[number];

/** Chi ha prodotto la voce: una funzione automatica, la skill di Simone, una persona dalla console. */
export const AUTORI_VOCE_REGISTRO = ['sistema', 'skill', 'console'] as const;
export type AutoreVoceRegistro = (typeof AUTORI_VOCE_REGISTRO)[number];

/** I codici del prossimo passo (le parole nei dizionari della console). */
export const PROSSIMI_PASSI = [
  'valutare_e_proporre',
  'attendere_risposta',
  'rispondere',
  'attendere_questionario',
  'attivare_accesso',
  'seguire_uso',
  'nessuno',
] as const;
export type ProssimoPasso = (typeof PROSSIMI_PASSI)[number];

/** `platform_leads/{id}/registro/{voceId}`. */
export interface VoceRegistroDoc<T = unknown> {
  tipo: TipoVoceRegistro;
  /** Quando è successo (non quando è stato scritto: un recupero può scrivere oggi una cosa di ieri). */
  quando: T;
  da: AutoreVoceRegistro;
  /** Una riga leggibile, nella lingua di chi gestisce la piattaforma (dato, non interfaccia). */
  riassunto: string;
  /** Riferimenti utili (id della risposta, del questionario, impronta dell'email…): solo stringhe. */
  rif?: { [nome: string]: string } | null;
  /** La chiave da cui nasce l'id (idempotenza). */
  chiave: string;
  creata_at: T;
}

/** I campi che il server aggiunge alla scheda (`platform_leads/{id}`) dopo ogni voce. */
export interface CampiLeadDalRegistro<T = unknown> {
  stato: StatoLeadPiattaforma;
  prossimo_passo: ProssimoPasso;
  registro_ultima: { tipo: TipoVoceRegistro; quando: T; riassunto: string } | null;
  registro_aggiornato_at: T;
}

/** La chiave di una voce: tipo + il riferimento che la rende unica (es. `email_conferma:<idRisposta>`). */
export function chiaveVoce(tipo: TipoVoceRegistro, riferimento: string): string {
  return `${tipo}:${riferimento}`;
}

/** Lo stato a cui porta ogni tipo di voce; i tipi assenti non spostano lo stato. */
const STATO_DOPO: { [tipo: string]: StatoLeadPiattaforma | undefined } = {
  iscrizione: 'nuovo',
  questionario_compilato: 'questionario_completato',
  messaggio: 'contattato',
  questionario_inviato: 'questionario_inviato',
  accesso_attivato: 'pilota',
  passato_a_pagamento: 'convertito',
  uscito: 'perso',
};

function posizione(stato: StatoLeadPiattaforma): number {
  return STATI_LEAD_PIATTAFORMA.indexOf(stato);
}

export function eStatoLead(valore: unknown): valore is StatoLeadPiattaforma {
  return typeof valore === 'string' && (STATI_LEAD_PIATTAFORMA as readonly string[]).indexOf(valore) >= 0;
}

export function eTipoVoce(valore: unknown): valore is TipoVoceRegistro {
  return typeof valore === 'string' && (TIPI_VOCE_REGISTRO as readonly string[]).indexOf(valore) >= 0;
}

/**
 * Lo stato dopo una voce. Lo stato va solo AVANTI nel percorso (una voce vecchia scritta tardi non lo
 * riporta indietro), con due eccezioni: chi esce dalla lista diventa `perso` da qualunque punto, tranne chi
 * paga già (`convertito` resta: è un cliente); e un `perso` torna nel percorso solo per un gesto SUO
 * (si iscrive di nuovo, compila un questionario, attiva l'accesso, paga), mai per un messaggio nostro.
 */
const GESTI_DELL_AZIENDA: readonly TipoVoceRegistro[] = ['iscrizione', 'questionario_compilato', 'accesso_attivato', 'passato_a_pagamento'];

export function statoDopoVoce(attuale: StatoLeadPiattaforma | undefined | null, tipo: TipoVoceRegistro): StatoLeadPiattaforma {
  const base: StatoLeadPiattaforma = attuale && eStatoLead(attuale) ? attuale : 'nuovo';
  const nuovo = STATO_DOPO[tipo];
  if (!nuovo) return base;
  if (nuovo === 'perso') return base === 'convertito' ? base : 'perso';
  if (base === 'perso') return GESTI_DELL_AZIENDA.indexOf(tipo) >= 0 ? nuovo : base;
  return posizione(nuovo) > posizione(base) ? nuovo : base;
}

/**
 * Il prossimo passo, dallo stato e dall'ultima voce. Una risposta dell'azienda chiede sempre una risposta
 * nostra, qualunque sia lo stato (finché non è chiuso).
 */
export function prossimoPasso(stato: StatoLeadPiattaforma, ultimo: TipoVoceRegistro | null): ProssimoPasso {
  if (stato === 'convertito' || stato === 'perso') return 'nessuno';
  if (ultimo === 'risposta_ricevuta') return 'rispondere';
  switch (stato) {
    case 'nuovo':
      return 'valutare_e_proporre';
    case 'contattato':
    case 'demo':
      return 'attendere_risposta';
    case 'questionario_inviato':
      return 'attendere_questionario';
    case 'questionario_completato':
      return 'attivare_accesso';
    case 'pilota':
      return 'seguire_uso';
    default:
      return 'nessuno';
  }
}
